import * as t from 'io-ts';

import {makeEnumRuntimeType} from '../../../../../models/api/apiTypes';
import {RFileAttachment} from '../../../../../models/api/attachmentModel';
import {CustomFieldsEnum} from '../../../../../models/api/customFieldModel';

/*
 * Runtime types.
 */

export enum ContactsManagerFileStateTypesEnum {
  ACCOUNTS_CSV = 'accounts-csv',
  CONTACTS_CSV = 'contacts-csv',
}
const ContactsManagerFileStateTypes = makeEnumRuntimeType(ContactsManagerFileStateTypesEnum);

export enum ContactsManagerCsvErrorsEnum {
  NO_HEADER = 'no_header',
  EMPTY_HEADERS = 'empty_headers',
  UNRECOGNIZED_HEADERS = 'unrecognized_headers',
  INVALID_DUPLICATE_HEADERS = 'invalid_duplicate_headers',
  TOO_MANY_ROWS = 'too_many_rows',
  TOO_MANY_COLUMNS = 'too_many_columns',
  INVALID_FILE_TYPE = 'invalid_file_type',
  PARSE_ERROR_MORE_ROWS_THAN_HEADER = 'parse_error_more_rows_than_header',
  MISSING_HEADER_COLUMN = 'missing_header_column',
  UNKNOWN_ERROR = 'unknown_error',
}
const RContactsManagerCsvErrors = makeEnumRuntimeType(ContactsManagerCsvErrorsEnum);

export enum ContactsManagerAccountsCsvErrorsEnum {
  MISSING_NAME = 'missing_name',
  MISSING_FIELDS = 'missing_fields',
  ROWS_ERRORS_WITH_DOWNLOAD = 'row_errors_with_download',
}
const RContactsManagerAccountsCSVErrors = makeEnumRuntimeType(ContactsManagerAccountsCsvErrorsEnum);

export enum ContactsManagerContactsCsvErrorsEnum {
  CSV_ROW_ERROR = 'csv_row_error',
}

export enum AccountsCsvStandardFieldsEnum {
  name = 'name',
  domains = 'domains',
  description = 'description',
  externalid = 'externalid',
}

const RContactsManagerContactsCsvErrors = makeEnumRuntimeType(ContactsManagerContactsCsvErrorsEnum);

const RContactsManagerFileErrorDirty = t.intersection([
  t.type({
    error: t.union([
      RContactsManagerCsvErrors,
      RContactsManagerAccountsCSVErrors,
      RContactsManagerContactsCsvErrors,
    ]),
  }),
  t.partial({
    row: t.number,
    columns: t.array(t.string),
  }),
]);

export interface ContactsManagerFileError extends Readonly<t.TypeOf<typeof RContactsManagerFileErrorDirty>> {}
export const RContactsManagerFileError = t.clean<ContactsManagerFileError>(
  RContactsManagerFileErrorDirty as any,
);

const RFileMetadata = t.type({
  headers: t.array(t.string),
  invalidStandardFieldCounts: t.record(t.string, t.number),
  invalidCustomFieldCounts: t.record(t.string, t.record(makeEnumRuntimeType(CustomFieldsEnum), t.number)),
});
export interface FileMetadata extends Readonly<t.TypeOf<typeof RFileMetadata>> {}

/*
 * Base State.
 */

export const RContactsManagerFileState = t.intersection([
  t.type({
    attachment: RFileAttachment,
    type: ContactsManagerFileStateTypes,
    file: t.unknown, // File type
  }),
  t.partial({
    errors: t.readonlyArray(RContactsManagerFileError),
  }),
]);

/*
 * Additional States.
 */

export const RContactsManagerAccountsCSVFileState = t.intersection([
  RContactsManagerFileState,
  t.type({
    type: t.literal(ContactsManagerFileStateTypesEnum.ACCOUNTS_CSV),
  }),
  t.partial({
    fileMetadata: RFileMetadata,
    warningFile: t.unknown,
    mapping: t.record(t.string, t.number),
  }),
]);

export const RContactsManagerContactsCsvFileState = t.intersection([
  RContactsManagerFileState,
  t.type({
    type: t.literal(ContactsManagerFileStateTypesEnum.CONTACTS_CSV),
  }),
]);

/*
 * Types.
 */

export interface ContactsManagerAccountsCsvFileState
  extends Readonly<t.TypeOf<typeof RContactsManagerAccountsCSVFileState>> {
  file: File;
}

export interface ContactsManagerContactsCsvFileState
  extends Readonly<t.TypeOf<typeof RContactsManagerContactsCsvFileState>> {
  file: File;
}

export type ContactsManagerFileState =
  | ContactsManagerAccountsCsvFileState
  | ContactsManagerContactsCsvFileState;

/*
 * Type guards.
 */

export function fileStateIsAccountsCSVFile(
  fileState: ContactsManagerFileState | undefined,
): fileState is ContactsManagerAccountsCsvFileState {
  return fileState?.type === ContactsManagerFileStateTypesEnum.ACCOUNTS_CSV;
}

export function fileStateIsContactsCsvFile(
  fileState: ContactsManagerFileState | undefined,
): fileState is ContactsManagerContactsCsvFileState {
  return fileState?.type === ContactsManagerFileStateTypesEnum.CONTACTS_CSV;
}
